import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, CheckCircle2, Lock, Clock } from "lucide-react";

/**
 * 10K30 Curriculum Video Placeholder Card
 * Embedded into 10K30 Dashboard + WeekDetail page.
 * States: LOCKED / PLACEHOLDER (Coming Soon) / PUBLISHED (Watch Now) / IN_PROGRESS (Continue Watching) / COMPLETE (Rewatch)
 * Never blocks TRAIN/WALK/EAT or week progression.
 *
 * Props:
 *   weekNumber (optional): if supplied, fetches that week's lesson. Otherwise fetches the current week.
 *   showTimeline (default true): render the "All 12 Lessons" strip beneath the current-lesson card.
 */
export default function CurriculumVideoCard({ weekNumber, showTimeline = true }) {
  const [data, setData] = useState(null);
  const [all, setAll] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);

  const load = async () => {
    try {
      if (weekNumber) {
        const [w, a] = await Promise.all([
          api.get(`/curriculum/video/${weekNumber}`),
          api.get("/curriculum/videos"),
        ]);
        setData(w.data);
        setCurrentWeek(a.data.current_week);
        setAll(a.data.videos || []);
      } else {
        const [c, a] = await Promise.all([
          api.get("/curriculum/video/current"),
          api.get("/curriculum/videos"),
        ]);
        setData(c.data);
        setCurrentWeek(a.data.current_week);
        setAll(a.data.videos || []);
      }
    } catch (e) { /* silently skip */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekNumber]);

  if (!data || !data.video) return null;
  const v = data.video;
  const unlocked = data.unlocked !== false;
  const complete = data.completion_status === "COMPLETE";
  const inProgress = data.completion_status === "IN_PROGRESS";
  const published = v.video_status === "PUBLISHED" && v.video_url_or_asset_id;

  let cta = { label: "COMING SOON", disabled: true, tone: "muted" };
  if (!unlocked) cta = { label: "LOCKED", disabled: true, tone: "muted" };
  else if (complete) cta = { label: "REWATCH", disabled: !published, tone: "primary" };
  else if (published && inProgress) cta = { label: "CONTINUE WATCHING", disabled: false, tone: "primary" };
  else if (published) cta = { label: "WATCH NOW", disabled: false, tone: "primary" };

  const openVideo = () => {
    if (!published || !v.video_url_or_asset_id) return;
    setPlaying(true);
  };

  const markComplete = async () => {
    setBusy(true);
    try {
      await api.post("/curriculum/video/complete", { week_number: v.week_number });
      toast.success("Lesson marked complete.");
      load();
    } catch (e) { toast.error("Could not mark complete."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid={`curriculum-video-${v.week_number}`}>
      {playing && published && (
        <div className="w-full bg-black" data-testid={`curriculum-player-${v.week_number}`}>
          <video src={v.video_url_or_asset_id} controls autoPlay playsInline
            className="w-full max-h-[70vh] block mx-auto" onEnded={() => setPlaying(false)}>
            Your browser does not support HTML5 video.
          </video>
          <div className="flex items-center justify-between px-4 py-2 bg-black/90 text-white/80 text-[10px] uppercase tracking-widest">
            <span>Now Playing · Week {v.week_number} · {v.video_title}</span>
            <button onClick={() => setPlaying(false)} className="hover:text-white" data-testid={`curriculum-close-${v.week_number}`}>Close</button>
          </div>
        </div>
      )}
      <div className="flex flex-col md:flex-row">
        {/* Thumbnail — uses uploaded thumbnail_asset when available, otherwise phase-branded placeholder */}
        <div className="md:w-64 aspect-video md:aspect-auto flex items-center justify-center relative shrink-0 overflow-hidden"
          style={v.thumbnail_asset
            ? { backgroundImage: `url(${v.thumbnail_asset})`, backgroundSize: "cover", backgroundPosition: "center" }
            : { background: `linear-gradient(135deg, ${v.accent}22 0%, ${v.accent}66 100%)` }}>
          {v.thumbnail_asset && published && (
            <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
          )}
          {published ? (
            <button onClick={openVideo}
              className="relative z-10 w-16 h-16 rounded-full bg-white/95 hover:bg-white flex items-center justify-center transition-colors shadow-lg"
              data-testid={`curriculum-play-${v.week_number}`}>
              <Play size={28} className="text-black ml-1" fill="currentColor" />
            </button>
          ) : !unlocked ? (
            <div className="relative z-10 flex flex-col items-center gap-2 text-white/90">
              <Lock size={28} />
              <span className="text-[10px] uppercase tracking-widest">Locked</span>
            </div>
          ) : (
            <div className="relative z-10 flex flex-col items-center gap-2 text-white/90 text-center px-3">
              <Clock size={28} />
              <span className="text-[10px] uppercase tracking-widest leading-tight">Video Placeholder<br/>Coming Soon</span>
            </div>
          )}
          {complete && (
            <div className="absolute top-2 right-2 z-20 bg-emerald-500 rounded-full p-1">
              <CheckCircle2 size={16} className="text-white" />
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 p-5 md:p-6 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{ color: v.accent }}>
            <span className="w-2 h-2 rounded-full" style={{ background: v.accent }} />
            Weekly Lesson · Week {v.week_number} · {v.phase.replace("_", " ")}
          </div>
          <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{v.module_title}</div>
          <h3 className="mt-2 font-head uppercase text-xl md:text-2xl leading-tight" data-testid={`curriculum-title-${v.week_number}`}>
            {v.video_title}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">{v.video_description}</p>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground/80">{v.duration}</div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button onClick={openVideo} disabled={cta.disabled}
              className={cta.tone === "primary" ? "btn-primary" : "btn-secondary opacity-60 cursor-not-allowed"}
              data-testid={`curriculum-cta-${v.week_number}`}>
              {cta.label}
            </button>
            {published && !complete && unlocked && (
              <button onClick={markComplete} disabled={busy}
                className="text-xs underline text-muted-foreground hover:text-foreground disabled:opacity-50"
                data-testid={`curriculum-mark-complete-${v.week_number}`}>
                Mark as complete
              </button>
            )}
            {complete && (
              <span className="text-[10px] uppercase tracking-widest text-emerald-500 flex items-center gap-1">
                <CheckCircle2 size={12} /> Lesson Complete
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Timeline — all 12 lessons */}
      {showTimeline && all.length > 0 && (
        <div className="border-t border-border p-5 md:p-6 bg-background/40">
          <div className="label-eyebrow mb-3">All 12 Weekly Lessons</div>
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-1.5" data-testid="curriculum-timeline">
            {all.map((item) => {
              const isCurrent = item.week_number === v.week_number;
              const isComplete = item.completion_status === "COMPLETE";
              const isLocked = !item.unlocked;
              return (
                <a key={item.week_number}
                  href={`/app/week/${item.week_number}`}
                  className={`rounded-md border p-2 flex flex-col items-center transition-colors ${
                    isLocked ? "opacity-50 pointer-events-none border-border" :
                    isCurrent ? "ring-2 border-transparent" :
                    "border-border hover:border-primary"
                  }`}
                  style={isCurrent ? { borderColor: item.accent, "--tw-ring-color": item.accent } : {}}
                  data-testid={`curriculum-timeline-${item.week_number}`}>
                  <span className="text-[9px] uppercase tracking-widest" style={{ color: item.accent }}>W{item.week_number}</span>
                  <span className="mt-0.5 text-[10px] uppercase tracking-widest">
                    {isLocked ? <Lock size={9} className="inline" /> :
                     isComplete ? <CheckCircle2 size={10} className="inline text-emerald-500" /> :
                     "•"}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
